import { ref } from 'vue'
import {
  finishManualService,
  getConversationList,
  getConversationMessages,
  getManualConversationMessages,
  getConversationStarters,
  getManualServiceQueue,
  sendManualReply,
  startOrContinueChat,
  submitCustomerServiceFeedback,
  takeOverConversation,
  transferToHuman,
} from '@/api/customerService'
import {
  normalizeCustomerServiceConversation,
  normalizeCustomerServiceMessage,
} from '@/utils/customerServiceContent'

export function useCustomerService() {
  let localMessageSeed = 0
  const conversations = ref([])
  const activeConversationId = ref(null)
  const messages = ref([])
  const manualQueue = ref([])
  const loadingConversations = ref(false)
  const loadingMessages = ref(false)
  const loadingStarters = ref(false)
  const loadingManualQueue = ref(false)
  const sending = ref(false)
  const transferingToHuman = ref(false)
  const initialized = ref(false)
  const starterPayload = ref({
    welcomeMessage: '',
    capabilityLabels: [],
    questions: [],
  })

  async function initialize() {
    if (initialized.value) {
      await loadConversations(true)
      return
    }

    await loadConversations(true)
    initialized.value = true
  }

  async function loadConversations(autoSelect = true) {
    loadingConversations.value = true
    try {
      const data = await getConversationList()
      conversations.value = Array.isArray(data)
        ? data.map(normalizeCustomerServiceConversation)
        : []

      const hasActiveConversation = conversations.value.some(
        item => item.conversationRecordId === activeConversationId.value
      )
      if (!hasActiveConversation) {
        activeConversationId.value = null
      }

      if (autoSelect && conversations.value.length > 0) {
        const nextConversationId = activeConversationId.value || conversations.value[0].conversationRecordId
        await selectConversation(nextConversationId)
      } else if (!conversations.value.length) {
        messages.value = []
      }
    } finally {
      loadingConversations.value = false
    }
  }

  async function selectConversation(conversationRecordId) {
    if (!conversationRecordId) {
      activeConversationId.value = null
      messages.value = []
      return
    }

    activeConversationId.value = conversationRecordId
    await loadMessages(conversationRecordId)
  }

  async function selectManualConversation(conversationRecordId) {
    if (!conversationRecordId) {
      activeConversationId.value = null
      messages.value = []
      return
    }

    activeConversationId.value = conversationRecordId
    await loadManualConversationMessages(conversationRecordId)
  }

  async function loadMessages(conversationRecordId = activeConversationId.value) {
    if (!conversationRecordId) {
      messages.value = []
      return
    }

    loadingMessages.value = true
    try {
      const data = await getConversationMessages(conversationRecordId)
      messages.value = Array.isArray(data)
        ? data.map(normalizeCustomerServiceMessage)
        : []
    } finally {
      loadingMessages.value = false
    }
  }

  async function loadManualConversationMessages(conversationRecordId = activeConversationId.value) {
    if (!conversationRecordId) {
      messages.value = []
      return
    }

    loadingMessages.value = true
    try {
      const data = await getManualConversationMessages(conversationRecordId)
      messages.value = Array.isArray(data)
        ? data.map(normalizeCustomerServiceMessage)
        : []
    } finally {
      loadingMessages.value = false
    }
  }

  function startNewConversation() {
    activeConversationId.value = null
    messages.value = []
  }

  async function loadConversationStarters(context = {}) {
    loadingStarters.value = true
    try {
      const data = await getConversationStarters({
        currentModule: context?.currentModule || '',
        currentPath: context?.currentPath || '',
        businessCode: context?.businessCode || '',
        recordId: context?.recordId || '',
        recordTitle: context?.recordTitle || '',
      })

      starterPayload.value = {
        welcomeMessage: data?.welcomeMessage || '',
        capabilityLabels: Array.isArray(data?.capabilityLabels) ? data.capabilityLabels : [],
        questions: Array.isArray(data?.questions) ? data.questions : [],
      }
    } finally {
      loadingStarters.value = false
    }
  }

  async function sendMessage(payload) {
    if (sending.value) {
      return false
    }

    const content = String(payload?.message || '').trim()
    if (!content) {
      return false
    }

    const pendingMessageId = createPendingUserMessage(content)
    sending.value = true
    try {
      const response = await startOrContinueChat({
        conversationRecordId: activeConversationId.value,
        message: content,
        currentModule: payload?.currentModule || '',
        currentPath: payload?.currentPath || '',
        businessCode: payload?.businessCode || '',
        recordId: payload?.recordId || '',
        recordTitle: payload?.recordTitle || '',
      })

      const conversation = normalizeCustomerServiceConversation(response?.conversation)
      if (!conversation) {
        removeMessageById(pendingMessageId)
        return false
      }

      activeConversationId.value = conversation.conversationRecordId
      upsertConversation(conversation)

      replacePendingUserMessage(pendingMessageId, normalizeCustomerServiceMessage(response?.userMessage))
      appendAssistantMessage(normalizeCustomerServiceMessage(response?.assistantMessage))

      return true
    } catch (error) {
      removeMessageById(pendingMessageId)
      throw error
    } finally {
      sending.value = false
    }
  }

  async function requestHumanService(payload = {}) {
    const conversationRecordId = payload?.conversationRecordId || activeConversationId.value
    if (!conversationRecordId || transferingToHuman.value) {
      return false
    }

    transferingToHuman.value = true
    try {
      const conversation = normalizeCustomerServiceConversation(await transferToHuman({
        conversationRecordId,
        transferReason: payload?.transferReason || '',
      }))

      if (!conversation?.conversationRecordId) {
        return false
      }

      activeConversationId.value = conversation.conversationRecordId
      upsertConversation(conversation)
      await loadMessages(conversation.conversationRecordId)
      return true
    } finally {
      transferingToHuman.value = false
    }
  }

  async function submitFeedback(messageId, rating) {
    if (!messageId || !rating) {
      return
    }

    await submitCustomerServiceFeedback({
      messageId,
      rating,
    })

    messages.value = messages.value.map(item => {
      if (item.messageId !== messageId) {
        return item
      }

      return {
        ...item,
        feedbackRating: rating,
      }
    })
  }

  async function loadManualServiceQueue() {
    loadingManualQueue.value = true
    try {
      const data = await getManualServiceQueue()
      manualQueue.value = Array.isArray(data)
        ? data.map(normalizeCustomerServiceConversation)
        : []
    } finally {
      loadingManualQueue.value = false
    }
  }

  async function takeOverManualConversation(conversationRecordId) {
    if (!conversationRecordId) {
      return false
    }

    const conversation = normalizeCustomerServiceConversation(await takeOverConversation({ conversationRecordId }))
    if (!conversation?.conversationRecordId) {
      return false
    }

    upsertManualQueueConversation(conversation)
    if (conversation.conversationRecordId === activeConversationId.value) {
      await loadManualConversationMessages(conversation.conversationRecordId)
    }

    return true
  }

  async function sendManualServiceReply(payload = {}) {
    const conversationRecordId = payload?.conversationRecordId || activeConversationId.value
    const message = String(payload?.message || '').trim()
    if (!conversationRecordId || !message) {
      return false
    }

    const reply = normalizeCustomerServiceMessage(await sendManualReply({
      conversationRecordId,
      message,
    }))

    if (!reply?.messageId) {
      return false
    }

    messages.value = [...messages.value, reply]
    await loadManualServiceQueue()
    return true
  }

  async function finishManualConversation(payload = {}) {
    const conversationRecordId = payload?.conversationRecordId || activeConversationId.value
    if (!conversationRecordId) {
      return false
    }

    const conversation = normalizeCustomerServiceConversation(await finishManualService({
      conversationRecordId,
      summary: payload?.summary || '',
    }))

    if (!conversation?.conversationRecordId) {
      return false
    }

    if (conversation.status === 10 || conversation.status === 20) {
      upsertManualQueueConversation(conversation)
    } else {
      removeManualQueueConversation(conversation.conversationRecordId)
    }

    if (conversation.conversationRecordId === activeConversationId.value) {
      await loadManualConversationMessages(conversation.conversationRecordId)
    }

    return true
  }

  function upsertConversation(conversation) {
    const index = conversations.value.findIndex(
      item => item.conversationRecordId === conversation.conversationRecordId
    )

    if (index === -1) {
      conversations.value = [conversation, ...conversations.value]
    } else {
      const next = [...conversations.value]
      next.splice(index, 1)
      conversations.value = [conversation, ...next]
    }
  }

  function createPendingUserMessage(content) {
    localMessageSeed += 1
    const pendingMessage = {
      messageId: `pending-user-${localMessageSeed}`,
      conversationRecordId: activeConversationId.value || 0,
      role: 'user',
      content,
      feedbackRating: '',
      sourceType: 'user',
      senderName: '我',
      createdAt: new Date().toISOString(),
    }

    messages.value = [...messages.value, pendingMessage]
    return pendingMessage.messageId
  }

  function replacePendingUserMessage(pendingMessageId, actualMessage) {
    if (!actualMessage?.messageId) {
      removeMessageById(pendingMessageId)
      return
    }

    const index = messages.value.findIndex(item => item.messageId === pendingMessageId)
    if (index === -1) {
      messages.value = [...messages.value, actualMessage]
      return
    }

    const nextMessages = [...messages.value]
    nextMessages.splice(index, 1, actualMessage)
    messages.value = nextMessages
  }

  function appendAssistantMessage(message) {
    if (!message?.messageId) {
      return
    }

    messages.value = [...messages.value, message]
  }

  function removeMessageById(messageId) {
    messages.value = messages.value.filter(item => item.messageId !== messageId)
  }

  function upsertManualQueueConversation(conversation) {
    const index = manualQueue.value.findIndex(
      item => item.conversationRecordId === conversation.conversationRecordId
    )

    if (index === -1) {
      manualQueue.value = [conversation, ...manualQueue.value]
    } else {
      const next = [...manualQueue.value]
      next.splice(index, 1)
      manualQueue.value = [conversation, ...next]
    }
  }

  function removeManualQueueConversation(conversationRecordId) {
    manualQueue.value = manualQueue.value.filter(
      item => item.conversationRecordId !== conversationRecordId
    )
  }

  return {
    activeConversationId,
    conversations,
    initialized,
    loadingConversations,
    loadingManualQueue,
    loadingMessages,
    loadingStarters,
    manualQueue,
    messages,
    starterPayload,
    sending,
    transferingToHuman,
    initialize,
    loadConversations,
    loadConversationStarters,
    loadManualConversationMessages,
    loadManualServiceQueue,
    loadMessages,
    requestHumanService,
    selectConversation,
    selectManualConversation,
    sendManualServiceReply,
    sendMessage,
    startNewConversation,
    submitFeedback,
    takeOverManualConversation,
    finishManualConversation,
  }
}
